import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductModule } from '@erp/shared-types';
import type { EmailImportance } from '@erp/rpc';
import { useGetModuleConfig } from '@/api/org/config/modules';
import useAuth from '@/api/auth/useAuth';
import { templateVariableMarkersToMustache } from '@/components/common/lexical-template-variables/TemplateVariableNode';
import {
  EMAIL_REGEX,
  makeRecipientId,
  type CustomRecipient,
  type RecipientRole,
  type SendFromOption,
} from '../email-composer/types';
import type { BulkSendDraft, BulkSendItem, BulkSendResult } from './types';

interface SendFromOverrideOption {
  key: string;
  label: string;
  sendFrom:
    | { mode: 'configured' }
    | { mode: 'user_mailbox'; mailboxUpn: string };
}

interface UseBulkItemSendFlowOptions<TItem extends BulkSendItem, TExtra> {
  items: TItem[];
  /** Value `extra` resets to whenever the engine navigates to a different item. */
  initialExtra: TExtra;
  /**
   * Called right before the engine switches the "current" item (or clears it
   * on close) — e.g. to reset an in-flight preview mutation so a stale PDF
   * doesn't flash before the new item's preview resolves.
   */
  onNavigate?: () => void;
}

/**
 * Generic step-through send engine shared by bulk AR email-send flows.
 * Owns: item navigation, recipients, subject/body/attachment drafts with
 * edited-baseline tracking, send-from/BCC/importance, per-item draft
 * snapshot+restore, and the send-all/summary bookkeeping. Does NOT call any
 * preview/send RPC and does NOT know what `extra` contains — the item table
 * UI, RPC calls, and payload construction stay owned by the consuming dialog.
 */
export function useBulkItemSendFlow<TItem extends BulkSendItem, TExtra>({
  items,
  initialExtra,
  onNavigate,
}: UseBulkItemSendFlowOptions<TItem, TExtra>) {
  const isSingle = items.length === 1;

  // ── Step-through state ──────────────────────────────────────────────────
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'review' | 'summary'>('review');
  const [results, setResults] = useState<Map<string, BulkSendResult>>(
    new Map(),
  );
  const [itemDrafts, setItemDrafts] = useState<
    Map<string, BulkSendDraft<TExtra>>
  >(new Map());

  const currentItem = items[currentIndex] ?? items[0];

  // ── Per-item transient state ────────────────────────────────────────────
  const [selectedEmails, setSelectedEmails] = useState<
    Map<string, RecipientRole>
  >(new Map());
  const [customRecipients, setCustomRecipients] = useState<CustomRecipient[]>(
    [],
  );
  const [subjectDraft, setSubjectDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [pdfFilenameDraft, setPdfFilenameDraft] = useState('');
  // "BCC me" defaults off — a clerk sending a batch doesn't want a copy of every
  // message in their inbox. Opt-in is per item and is not persisted anywhere.
  const [bccSender, setBccSender] = useState(false);
  const [selectedSendFromKey, setSelectedSendFromKey] = useState('');
  const [extra, setExtra] = useState<TExtra>(initialExtra);

  // Dialog-level (not per-item) so a "mark this batch urgent" choice persists
  // as the clerk pages through items.
  const [importance, setImportance] = useState<EmailImportance>('normal');

  // Platform-generated baselines, used only to compute the `editedBody`
  // telemetry flag. The body baseline is stored in mustache form so
  // auto-updated template-variable values don't read as edits.
  const seededSubjectRef = useRef('');
  const baselineBodyMustacheRef = useRef('');
  const bodyBaselineCapturedItems = useRef<Set<string>>(new Set());
  const restoredDraftItems = useRef<Set<string>>(new Set());

  function handleBodyChange(html: string) {
    setBodyDraft(html);
    const id = currentItem?.id;
    if (id && !bodyBaselineCapturedItems.current.has(id)) {
      bodyBaselineCapturedItems.current.add(id);
      baselineBodyMustacheRef.current =
        templateVariableMarkersToMustache(html).trim();
    }
  }

  const editedBody = useMemo(() => {
    const subjectChanged =
      subjectDraft.trim() !== seededSubjectRef.current.trim();
    const baseline = baselineBodyMustacheRef.current.trim();
    const bodyChanged =
      baseline !== '' &&
      templateVariableMarkersToMustache(bodyDraft).trim() !== baseline;
    return subjectChanged || bodyChanged;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs intentionally omitted
  }, [subjectDraft, bodyDraft]);

  // ── Send-from options ───────────────────────────────────────────────────
  const { data: arModuleConfig } = useGetModuleConfig(
    ProductModule.AR_INVOICING,
  );
  const { currentUser } = useAuth();

  const sendFromOptions = useMemo<SendFromOverrideOption[]>(() => {
    const options: SendFromOverrideOption[] = [];
    const arConfig = arModuleConfig?.config?.ar;
    if (arConfig?.outboxMailboxUpn) {
      options.push({
        key: 'configured',
        label: arConfig.outboxSenderAddress ?? arConfig.outboxMailboxUpn,
        sendFrom: { mode: 'configured' },
      });
    }
    const userEmail = currentUser?.emails[0];
    if (userEmail) {
      options.push({
        key: `user:${userEmail.toLowerCase()}`,
        label: userEmail,
        sendFrom: { mode: 'user_mailbox', mailboxUpn: userEmail },
      });
    }
    return options;
  }, [arModuleConfig, currentUser]);

  useEffect(() => {
    if (sendFromOptions.length === 0) {
      setSelectedSendFromKey('');
      return;
    }
    if (!sendFromOptions.some((o) => o.key === selectedSendFromKey)) {
      setSelectedSendFromKey(sendFromOptions[0].key);
    }
  }, [sendFromOptions, selectedSendFromKey]);

  const selectedSendFrom = useMemo(
    () => sendFromOptions.find((o) => o.key === selectedSendFromKey)?.sendFrom,
    [sendFromOptions, selectedSendFromKey],
  );

  const sendFromOptionsForPanel = useMemo<SendFromOption[]>(
    () => sendFromOptions.map((o) => ({ key: o.key, label: o.label })),
    [sendFromOptions],
  );

  const getSendFromForKey = useCallback(
    (key: string) => sendFromOptions.find((o) => o.key === key)?.sendFrom,
    [sendFromOptions],
  );

  // ── Recipients logic ────────────────────────────────────────────────────

  function addContactToRole(email: string, role: RecipientRole) {
    setSelectedEmails((prev) => {
      if (prev.get(email) === role) return prev;
      const next = new Map(prev);
      next.set(email, role);
      return next;
    });
  }

  function toggleEmail(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      if (next.has(email)) {
        next.delete(email);
        if (![...next.values()].includes('to') && next.size > 0) {
          const firstKey = next.keys().next().value;
          if (firstKey !== undefined) next.set(firstKey, 'to');
        }
      } else {
        next.set(email, next.size === 0 ? 'to' : 'cc');
      }
      return next;
    });
  }

  function toggleEmailRole(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      const current = next.get(email);
      if (!current) return prev;
      if (
        current === 'to' &&
        [...prev.values()].filter((v) => v === 'to').length === 1
      ) {
        return prev;
      }
      next.set(email, current === 'to' ? 'cc' : 'to');
      return next;
    });
  }

  function removeContact(email: string) {
    setSelectedEmails((prev) => {
      if (!prev.has(email)) return prev;
      const next = new Map(prev);
      next.delete(email);
      if (![...next.values()].includes('to') && next.size > 0) {
        const firstKey = next.keys().next().value;
        if (firstKey !== undefined) next.set(firstKey, 'to');
      }
      return next;
    });
  }

  function addCustomRecipient(email: string, role: RecipientRole): boolean {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) return false;
    const lower = trimmed.toLowerCase();
    if (selectedEmails.has(lower)) return false;
    if (customRecipients.some((r) => r.email.toLowerCase() === lower))
      return false;
    setCustomRecipients((prev) => [
      ...prev,
      { id: makeRecipientId(), email: trimmed, role },
    ]);
    return true;
  }

  function removeCustomRecipient(id: string) {
    setCustomRecipients((prev) => prev.filter((r) => r.id !== id));
  }

  const validCustomRecipients = customRecipients.filter((r) =>
    EMAIL_REGEX.test(r.email.trim()),
  );
  const selectedToEmails = [...selectedEmails.entries()]
    .filter(([, role]) => role === 'to')
    .map(([email]) => email);
  const customToEmails = validCustomRecipients
    .filter((r) => r.role === 'to')
    .map((r) => r.email.trim());
  const customCcEmails = validCustomRecipients
    .filter((r) => r.role === 'cc')
    .map((r) => r.email.trim());
  const customBccEmails = validCustomRecipients
    .filter((r) => r.role === 'bcc')
    .map((r) => r.email.trim());
  const toEmails = [...selectedToEmails, ...customToEmails];
  const ccEmails = [
    ...[...selectedEmails.entries()]
      .filter(([, role]) => role === 'cc')
      .map(([email]) => email),
    ...customCcEmails,
  ];
  const bccEmails = [
    ...[...selectedEmails.entries()]
      .filter(([, role]) => role === 'bcc')
      .map(([email]) => email),
    ...customBccEmails,
  ];
  const firstCustomEmail = validCustomRecipients[0]?.email.trim();
  const allRecipients = [...toEmails, ...ccEmails];
  const hasRecipients = allRecipients.length > 0;

  // ── Draft capture / restore ─────────────────────────────────────────────

  const captureCurrentDraft = useCallback(
    (): BulkSendDraft<TExtra> => ({
      selectedEmails: new Map(selectedEmails),
      customRecipients: customRecipients.map((r) => ({ ...r })),
      subjectDraft,
      bodyDraft,
      pdfFilenameDraft,
      bccSender,
      selectedSendFromKey,
      seededSubject: seededSubjectRef.current,
      baselineBodyMustache: baselineBodyMustacheRef.current,
      extra,
    }),
    [
      selectedEmails,
      customRecipients,
      subjectDraft,
      bodyDraft,
      pdfFilenameDraft,
      bccSender,
      selectedSendFromKey,
      extra,
    ],
  );

  const persistCurrentDraft = useCallback(() => {
    if (!currentItem) return;
    const draft = captureCurrentDraft();
    setItemDrafts((prev) => {
      const next = new Map(prev);
      next.set(currentItem.id, draft);
      return next;
    });
  }, [currentItem, captureCurrentDraft]);

  const getDraft = useCallback(
    (itemId: string) => itemDrafts.get(itemId),
    [itemDrafts],
  );

  const hasDraftFor = useCallback(
    (itemId: string) => itemDrafts.has(itemId),
    [itemDrafts],
  );

  // Restore a saved draft when entering an item that has one. Gated by
  // itemId (not index) so a fresh item sharing an index with a previously
  // drafted one doesn't leak state.
  useEffect(() => {
    if (phase !== 'review' || !currentItem) return;
    const id = currentItem.id;
    if (restoredDraftItems.current.has(id)) return;
    const draft = itemDrafts.get(id);
    if (!draft) return;
    restoredDraftItems.current.add(id);
    setSelectedEmails(new Map(draft.selectedEmails));
    setCustomRecipients(draft.customRecipients.map((r) => ({ ...r })));
    setSubjectDraft(draft.subjectDraft);
    setBodyDraft(draft.bodyDraft);
    setPdfFilenameDraft(draft.pdfFilenameDraft);
    setBccSender(draft.bccSender);
    setSelectedSendFromKey(draft.selectedSendFromKey);
    setExtra(draft.extra);
    seededSubjectRef.current = draft.seededSubject;
    baselineBodyMustacheRef.current = draft.baselineBodyMustache;
    bodyBaselineCapturedItems.current.add(id);
  }, [phase, currentItem, itemDrafts]);

  function resetTransientState() {
    setSelectedEmails(new Map());
    setCustomRecipients([]);
    setSubjectDraft('');
    setBodyDraft('');
    setPdfFilenameDraft('');
    setBccSender(false);
    setExtra(initialExtra);
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  function findPrevUnsentIndex(fromIndex: number): number | null {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (results.get(items[i].id)?.status === 'sent') continue;
      return i;
    }
    return null;
  }

  function findNextUnsentIndex(fromIndex: number): number | null {
    for (let i = fromIndex + 1; i < items.length; i++) {
      if (!results.has(items[i].id)) return i;
    }
    for (let i = 0; i < fromIndex; i++) {
      if (!results.has(items[i].id)) return i;
    }
    return null;
  }

  const prevUnsentIndex = findPrevUnsentIndex(currentIndex);
  const canGoBack = !isSingle && prevUnsentIndex !== null;

  function advanceToNext() {
    persistCurrentDraft();
    onNavigate?.();
    resetTransientState();
    const nextIdx = findNextUnsentIndex(currentIndex);
    if (nextIdx !== null) {
      setCurrentIndex(nextIdx);
    } else {
      setPhase('summary');
    }
  }

  function goBack() {
    if (prevUnsentIndex === null) return;
    persistCurrentDraft();
    onNavigate?.();
    resetTransientState();
    setCurrentIndex(prevUnsentIndex);
  }

  function viewItem(index: number) {
    if (index < 0 || index >= items.length) return;
    if (index === currentIndex && phase === 'review') return;
    persistCurrentDraft();
    onNavigate?.();
    resetTransientState();
    setCurrentIndex(index);
    setPhase('review');
  }

  function revisitItem(index: number) {
    if (index < 0 || index >= items.length) return;
    const item = items[index];
    setResults((prev) => {
      const next = new Map(prev);
      next.delete(item.id);
      return next;
    });
    restoredDraftItems.current.delete(item.id);
    bodyBaselineCapturedItems.current.delete(item.id);
    onNavigate?.();
    resetTransientState();
    setCurrentIndex(index);
    setPhase('review');
  }

  function recordResult(itemId: string, result: BulkSendResult) {
    setResults((prev) => {
      const next = new Map(prev);
      next.set(itemId, result);
      return next;
    });
  }

  function handleSkip() {
    if (!currentItem) return;
    recordResult(currentItem.id, {
      status: 'skipped',
      itemName: currentItem.displayName,
    });
    advanceToNext();
  }

  function handleCancel() {
    if (!currentItem) return;
    recordResult(currentItem.id, {
      status: 'canceled',
      itemName: currentItem.displayName,
    });
    advanceToNext();
  }

  // ── Send-all bookkeeping (dialog drives the actual loop) ────────────────
  const [isSendingAll, setIsSendingAll] = useState(false);
  const [bulkSendProgress, setBulkSendProgress] = useState(0);
  const [showSendAllConfirm, setShowSendAllConfirm] = useState(false);

  // ── Lifecycle ────────────────────────────────────────────────────────────

  function resetAll() {
    setCurrentIndex(0);
    setPhase('review');
    setResults(new Map());
    setItemDrafts(new Map());
    setShowSendAllConfirm(false);
    setIsSendingAll(false);
    setBulkSendProgress(0);
    setImportance('normal');
    setSelectedSendFromKey('');
    restoredDraftItems.current.clear();
    bodyBaselineCapturedItems.current.clear();
    resetTransientState();
    onNavigate?.();
  }

  return {
    isSingle,
    currentIndex,
    currentItem,
    phase,
    setPhase,
    results,
    recordResult,
    itemDrafts,

    selectedEmails,
    customRecipients,
    addContactToRole,
    toggleEmail,
    toggleEmailRole,
    removeContact,
    addCustomRecipient,
    removeCustomRecipient,
    toEmails,
    ccEmails,
    bccEmails,
    firstCustomEmail,
    allRecipients,
    hasRecipients,

    sendFromOptions: sendFromOptionsForPanel,
    selectedSendFromKey,
    setSelectedSendFromKey,
    selectedSendFrom,
    getSendFromForKey,
    bccSender,
    setBccSender,
    importance,
    setImportance,

    subjectDraft,
    setSubjectDraft,
    bodyDraft,
    setBodyDraft,
    handleBodyChange,
    pdfFilenameDraft,
    setPdfFilenameDraft,
    editedBody,
    seededSubjectRef,

    extra,
    setExtra,

    captureCurrentDraft,
    persistCurrentDraft,
    getDraft,
    hasDraftFor,
    resetTransientState,

    goBack,
    canGoBack,
    advanceToNext,
    handleSkip,
    handleCancel,
    viewItem,
    revisitItem,

    isSendingAll,
    setIsSendingAll,
    bulkSendProgress,
    setBulkSendProgress,
    showSendAllConfirm,
    setShowSendAllConfirm,

    resetAll,
  };
}
